import React from "react";
import { motion } from "framer-motion";
import { resolveIconSrc } from "./builder/iconMap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Label,
} from "recharts";
import Plot from "react-plotly.js";
import { Palette, Settings2, TrendingUp, CircleDot, Type, Undo, Redo } from "lucide-react";
import { mergeMapboxConfig } from "@/lib/plotly-mapbox-config";
import { BarChart, Bar, ErrorBar } from "recharts";
import { useAnimation } from "framer-motion";
import LandingChatDemo from "./lumichat";

export default function DemoWindowPlot({
  width = 680,
  height = 560,
  centered = true,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
}) {
  const data = React.useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const t = i / 3;
        const base = 40 + 8 * Math.sin(t) + 3 * Math.cos(t * 2.3);
        return {
          hour: `${i}:00`,
          variantA: Math.round(base + 6 * Math.sin(t * 0.8 + 0.6)),
          variantB: Math.round(base - 4 * Math.cos(t * 1.1 - 0.8)),
        };
      }),
    []
  );

  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={[
        "group relative select-none overflow-hidden rounded-xl",
        "shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]",
        "border border-black/10 bg-white/95 backdrop-blur",
        centered ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "",
      ].join(" ")}
      style={{ width, height, zIndex: 10 }}
    >
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Lumiplot
        </div>
        <div className="-mr-1 flex items-center">
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
            disabled
          >
            Open app
          </button>
        </div>
      </div>

      <div className="flex h-full w-full">
        <div className="min-w-0 border-r border-black/10" style={{ flexBasis: 0, flexGrow: 6 }}>
          <div 
            className="relative h-full w-full overflow-auto overscroll-contain bg-neutral-50/60 p-3 font-mono text-[12px] leading-relaxed text-neutral-800"
            style={{ overscrollBehaviorY: 'contain', scrollbarGutter: 'stable both-edges' }}
          >
            <pre className="whitespace-pre-wrap">{
`# Lumiplot auto-generated code\nimport pandas as pd\nimport numpy as np\nimport matplotlib.pyplot as plt\n\nnp.random.seed(7)\nt = np.arange(0, 24)\na = 40 + 8*np.sin(t/3) + np.random.normal(0, 2, size=t.size)\nb = 40 - 4*np.cos(1.1*t) + np.random.normal(0, 2, size=t.size)\n\nfig, ax = plt.subplots(figsize=(6,3))\nax.plot(t, a, label='Variant A')\nax.plot(t, b, label='Variant B')\nax.set(xlabel='hour', ylabel='value')\nax.grid(alpha=0.2)\nax.legend()\n`}
            </pre>
            <div className="pointer-events-none absolute right-3 top-3 rounded-full border border-black/10 bg-white/80 px-2 py-0.5 text-[11px] text-neutral-700 shadow-sm">
              AI generated
            </div>
          </div>
        </div>

        <div className="min-w-0" style={{ flexBasis: 0, flexGrow: 7 }}>
          <div className="relative h-full w-full bg-white">
            <div className="absolute inset-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 18, right: 24, left: 8, bottom: 12 }}>
                  <defs>
                    <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="currentColor" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="currentColor" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gradB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="currentColor" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="currentColor" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeOpacity={0.15} />
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={28} />
                  <Tooltip contentStyle={{ fontSize: 12 }} wrapperStyle={{ outline: "none" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="variantA" name="Variant A" strokeWidth={2} stroke="currentColor" fill="url(#gradA)" />
                  <Area type="monotone" dataKey="variantB" name="Variant B" strokeWidth={2} stroke="currentColor" fill="url(#gradB)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/85 px-2 py-0.5 text-[11px] text-neutral-800 shadow-sm">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-neutral-800" />
              n = 24 · mean ≈ 40.7
            </div>
            <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-white/80 px-1.5 py-0.5 text-[11px] text-neutral-700 shadow-sm">
              hour
            </div>
            <div className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 rotate-[-90deg] rounded bg-white/80 px-1.5 py-0.5 text-[11px] text-neutral-700 shadow-sm">
              value
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function normalCdf(z: number) {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp(-z * z / 2);
  let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  if (z > 0) p = 1 - p;
  return p;
}

function makeSyntheticPoints(n = 80) {
  const pts: { id: string; group: string; x: number; y: number; label: string }[] = [];
  const groups = ["Group Alpha", "Group Beta"];
  for (let i = 0; i < n; i++) {
    const g = i % 3 === 0 ? groups[1] : groups[0];
    const x = Math.random() * 70;
    const trueSlope = -0.0085;
    const yBase = 0.95 + trueSlope * x;
    const noise = (g === groups[1] ? 0.07 : 0.05) * (Math.random() - 0.5) * 2;
    const y = yBase + noise;
    const label = `Sample ${i + 1}`;
    pts.push({ id: `P${i + 1}`, group: g, x, y, label });
  }
  return pts.sort((a, b) => a.x - b.x);
}

function computeRegression(pts: Array<{ x: number; y: number }>) {
  const n = pts.length;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const xbar = mean(xs);
  const ybar = mean(ys);
  let sxx = 0, syy = 0, sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - xbar;
    const dy = ys[i] - ybar;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }
  const slope = sxy / sxx;
  const intercept = ybar - slope * xbar;
  const r = sxy / Math.sqrt(sxx * syy);

  const residuals = pts.map((p) => p.y - (intercept + slope * p.x));
  const s = Math.sqrt(residuals.reduce((a, b) => a + b * b, 0) / (n - 2));

  const z = 0.5 * Math.log((1 + r) / (1 - r)) * Math.sqrt(n - 3);
  const p = 2 * (1 - normalCdf(Math.abs(z)));

  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const grid = Array.from({ length: 100 }, (_, i) => xMin + (i * (xMax - xMin)) / 99);

  const upper: number[] = [];
  const lower: number[] = [];
  const yhat: number[] = [];
  for (const xg of grid) {
    const yh = intercept + slope * xg;
    const se = s * Math.sqrt(1 / n + Math.pow(xg - xbar, 2) / sxx);
    const m = 1.96;
    yhat.push(yh);
    upper.push(yh + m * se);
    lower.push(yh - m * se);
  }

  return { slope, intercept, r, p, s, xbar, ybar, grid, yhat, upper, lower, residuals };
}

export function ScientificPlotWindow() {
  const pts = React.useMemo(() => makeSyntheticPoints(90), []);
  const stats = React.useMemo(() => computeRegression(pts), [pts]);

  const outlierFlags = React.useMemo(() => {
    return stats.residuals.map((e) => Math.abs(e / stats.s) > 2);
  }, [stats]);

  const pointsAlpha = pts.filter((_, i) => pts[i].group === "Group Alpha" && !outlierFlags[i]);
  const pointsBeta = pts.filter((_, i) => pts[i].group === "Group Beta" && !outlierFlags[i]);
  const outliers = pts.filter((_, i) => outlierFlags[i]);

  const ciX = [...stats.grid, ...stats.grid.slice().reverse()];
  const ciY = [...stats.upper, ...stats.lower.slice().reverse()];

  const lineTrace = {
    x: stats.grid,
    y: stats.yhat,
    type: "scatter" as const,
    mode: "lines" as const,
    name: "Fit line",
    hovertemplate: "<b>Fit line</b><br>y = %{y:.3f}<extra></extra>",
    line: { width: 2, color: "#ffd500" },
  };

  const ciTrace = {
    x: ciX,
    y: ciY,
    type: "scatter" as const,
    mode: "lines" as const,
    name: "95% CI",
    hoveron: "fills" as const,
    hovertemplate: "95% CI of mean<extra></extra>",
    line: { width: 0 },
    fill: "toself" as const,
    fillcolor: "rgba(17,17,17,0.08)",
    showlegend: true,
  };

  const alphaTrace = {
    x: pointsAlpha.map((p) => p.x),
    y: pointsAlpha.map((p) => p.y),
    type: "scatter" as const,
    mode: "markers" as const,
    name: "Group Alpha",
    text: pointsAlpha.map((p) => p.label),
    hovertemplate: "<b>%{text}</b><br>Group: %{meta}<br>x: %{x:.2f}<br>y: %{y:.3f}<extra></extra>",
    meta: "Group Alpha",
    marker: { size: 6, color: "#6b7280", line: { width: 0.5, color: "#374151" } },
  };

  const betaTrace = {
    x: pointsBeta.map((p) => p.x),
    y: pointsBeta.map((p) => p.y),
    type: "scatter" as const,
    mode: "markers" as const,
    name: "Group Beta",
    text: pointsBeta.map((p) => p.label),
    hovertemplate: "<b>%{text}</b><br>Group: %{meta}<br>x: %{x:.2f}<br>y: %{y:.3f}<extra></extra>",
    meta: "Group Beta",
    marker: { size: 6, color: "#9ca3af", line: { width: 0.5, color: "#4b5563" } },
  };

  const outlierTrace = {
    x: outliers.map((p) => p.x),
    y: outliers.map((p) => p.y),
    type: "scatter" as const,
    mode: "markers" as const,
    name: "Outliers",
    text: outliers.map((p) => `${p.label} · possible outlier`),
    hovertemplate: "<b>%{text}</b><br>x: %{x:.2f}<br>y: %{y:.3f}<extra></extra>",
    marker: { size: 8, symbol: "circle-open", color: "#111827", line: { width: 1.5, color: "#111827" } },
  };

  const fig = {
    data: [ciTrace, lineTrace, alphaTrace, betaTrace, outlierTrace],
    layout: {
      autosize: true,
      margin: { l: 48, r: 28, t: 24, b: 40 },
      hovermode: "closest" as const,
      showlegend: true,
      legend: { orientation: "h" as const, x: 0, y: 1.12, font: { size: 11 } },
      xaxis: {
        title: { text: "M-value (a.u.)", font: { size: 12 } },
        gridcolor: "rgba(0,0,0,0.1)",
        zeroline: false,
      },
      yaxis: {
        title: { text: "Response (ratio)", font: { size: 12 } },
        gridcolor: "rgba(0,0,0,0.1)",
        zeroline: false,
      },
      paper_bgcolor: "rgba(255,255,255,0)",
      plot_bgcolor: "rgba(255,255,255,0)",
      annotations: [
        {
          x: 60,
          y: 0.95,
          xref: "x",
          yref: "paper",
          xanchor: "center",
          yanchor: "top",
          align: "center",
          showarrow: false,
          font: { size: 11, color: "#111827" },
          text: `r = ${stats.r.toFixed(2)}<br>t ≈ ${(stats.r * Math.sqrt((pts.length - 2) / (1 - stats.r * stats.r))).toFixed(2)}<br>p ≈ ${stats.p.toExponential(2)}`,
        },
      ],
    },
    config: {
      displayModeBar: false,
      responsive: true,
      toImageButtonOptions: { filename: "lumiplot-scientific-demo" },
    },
  } as any;

  return (
    <div className="group relative select-none overflow-hidden rounded-[10px] border border-black/10 bg-white/95 shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]">
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Scientific Scatter
        </div>
        <div className="-mr-1 flex items-center">
          <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100" disabled>
            Open app
          </button>
        </div>
      </div>
      <div className="h-[420px] w-full p-2 text-neutral-800">
        <Plot style={{ width: "100%", height: "100%" }} data={fig.data as any} layout={fig.layout as any} config={mergeMapboxConfig(fig.config)} />
      </div>
    </div>
  );
}

export function ScientificBoxPlotMultiWindow() {
  function sample(n: number, mean: number, sd: number) {
    return Array.from({ length: n }, () => mean + sd * (Math.random() * 2 - 1));
  }
  const groups = ["Control", "Low dose", "High dose"] as const;
  const yA = React.useMemo(() => sample(42, 0.02, 0.22), []);
  const yB = React.useMemo(() => sample(44, 0.20, 0.25), []);
  const yC = React.useMemo(() => sample(46, 0.45, 0.28), []);
  const all = [yA, yB, yC];

  const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const median = (arr: number[]) => {
    const s = sortNums(arr);
    const n = s.length;
    return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
  };
  const sortNums = (arr: number[]) => [...arr].sort((a, b) => a - b);

  function normalCdf(z: number) {
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989423 * Math.exp(-z * z / 2);
    let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    if (z > 0) p = 1 - p;
    return p;
  }

  function ranks(values: number[]) {
    const pairs = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
    const r: number[] = Array(values.length).fill(0);
    for (let i = 0; i < pairs.length; ) {
      let j = i + 1;
      while (j < pairs.length && pairs[j].v === pairs[i].v) j++;
      const avg = (i + 1 + j) / 2;
      for (let k = i; k < j; k++) r[pairs[k].i] = avg;
      i = j;
    }
    return r;
  }

  function mannWhitney(a: number[], b: number[]) {
    const combined = a.concat(b);
    const r = ranks(combined);
    const na = a.length, nb = b.length, N = na + nb;
    const RA = r.slice(0, na).reduce((s, v) => s + v, 0);
    const U1 = RA - (na * (na + 1)) / 2;
    const U2 = na * nb - U1;
    const U = Math.min(U1, U2);
    const sorted = sortNums(combined);
    let sumT = 0;
    for (let i = 0; i < N; ) {
      let j = i + 1;
      while (j < N && sorted[j] === sorted[i]) j++;
      const t = j - i;
      if (t > 1) sumT += t * t * t - t;
      i = j;
    }
    const tie = sumT / (N * (N - 1));
    const meanU = (na * nb) / 2;
    const varU = (na * nb / 12) * (N + 1 - tie);
    const z = (U - meanU + 0.5 * Math.sign(meanU - U)) / Math.sqrt(varU);
    const p = 2 * (1 - normalCdf(Math.abs(z)));
    return { U, z, p, na, nb };
  }

  function wilcoxonSignedRankVsZero(x: number[]) {
    const diffs = x.filter((v) => v !== 0);
    const n = diffs.length;
    if (n === 0) return { W: 0, z: 0, p: 1, n };
    const abs = diffs.map((v) => Math.abs(v));
    const r = ranks(abs);
    let Wplus = 0;
    for (let i = 0; i < n; i++) if (diffs[i] > 0) Wplus += r[i];
    const meanW = n * (n + 1) / 4;
    const varW = n * (n + 1) * (2 * n + 1) / 24;
    const z = (Wplus - meanW - 0.5 * Math.sign(Wplus - meanW)) / Math.sqrt(varW);
    const p = 2 * (1 - normalCdf(Math.abs(z)));
    return { W: Wplus, z, p, n };
  }

  const star = (p: number) => (p < 1e-3 ? "***" : p < 1e-2 ? "**" : p < 5e-2 ? "*" : "ns");

  const mkBox = (name: string, ys: number[], fill?: string) => ({
    type: "box",
    name,
    x: Array(ys.length).fill(name),
    y: ys,
    boxpoints: "outliers",
    marker: { color: "#6b7280", line: { color: "#374151", width: 1 } },
    line: { color: "#111827", width: 1 },
    fillcolor: fill ?? "rgba(156,163,175,0.18)",
    text: ys.map((_, i) => `${name[0]}-${String(i + 1).padStart(2, "0")}`),
    hovertemplate:
      `<b>${name}</b>` +
      `<br>median: %{median:.2f}` +
      `<br>q1: %{q1:.2f}` +
      `<br>q3: %{q3:.2f}` +
      `<br>min: %{min:.2f}` +
      `<br>max: %{max:.2f}<extra></extra>`,
  });

  const control = mkBox(groups[0], yA);
  const lowDose = mkBox(groups[1], yB, "rgba(255,213,0,0.20)");
  const highDose = mkBox(groups[2], yC, "rgba(255,213,0,0.30)");

  const refLine: any = {
    type: "scatter",
    mode: "lines",
    name: "Reference line",
    x: [groups[0], groups[1], groups[2]],
    y: [0, 0, 0],
    line: { width: 1.5, dash: "dot", color: "#9ca3af" },
    hovertemplate: "<b>Reference line</b><br>y = 0<extra></extra>",
    showlegend: false,
  };

  const maxima = [Math.max(...yA), Math.max(...yB), Math.max(...yC)];
  const starY = Math.max(...maxima) + 0.18;
  const testsVs0 = [yA, yB, yC].map(wilcoxonSignedRankVsZero);
  const starTexts = testsVs0.map((t) => star(t.p));
  const starHoverTexts = [groups[0], groups[1], groups[2]].map((g, i) => (
    `${g}: Wilcoxon vs 0 (n=${testsVs0[i].n})` +
    `<br><b>median</b> = ${median(all[i]).toFixed(2)}` +
    `<br><b>W</b> = ${testsVs0[i].W.toFixed(1)}, <b>z</b> = ${testsVs0[i].z.toFixed(2)}, <b>p</b> = ${testsVs0[i].p.toExponential(2)}`
  ));

  const starMarkers: any = {
    type: "scatter",
    mode: "text",
    x: [groups[0], groups[1], groups[2]],
    y: [starY, starY, starY],
    text: starTexts,
    customdata: starHoverTexts,
    textfont: { size: 16, color: "#ffd500", family: ((typeof window !== 'undefined' && (window as any).__lumiUiSansStack) || 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif') },
    hovertemplate: "%{customdata}<extra></extra>",
    showlegend: false,
  } as any;

  const mwLowHigh = mannWhitney(yB, yC);
  const starMW = star(mwLowHigh.p);
  const yTop = starY + 0.12;
  const h = 0.06;
  const bracketLeft: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Mann–Whitney U<br>U = ${mwLowHigh.U.toFixed(1)}<br>z = ${mwLowHigh.z.toFixed(2)}<br>p = ${mwLowHigh.p.toExponential(2)}<extra></extra>`,
    x: [groups[1], groups[1]],
    y: [yTop - h, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };
  const bracketMid: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Mann–Whitney U<br>U = ${mwLowHigh.U.toFixed(1)}<br>z = ${mwLowHigh.z.toFixed(2)}<br>p = ${mwLowHigh.p.toExponential(2)}<extra></extra>`,
    x: [groups[1], groups[2]],
    y: [yTop, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };
  const bracketRight: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Mann–Whitney U<br>U = ${mwLowHigh.U.toFixed(1)}<br>z = ${mwLowHigh.z.toFixed(2)}<br>p = ${mwLowHigh.p.toExponential(2)}<extra></extra>`,
    x: [groups[2], groups[2]],
    y: [yTop - h, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };
  const bracketStars: any = {
    type: "scatter",
    mode: "text",
    x: [groups[1]],
    y: [yTop + 0.03],
    text: [`${starMW}`],
    textfont: { size: 16, color: "#111827", family: ((typeof window !== 'undefined' && (window as any).__lumiUiSansStack) || 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif') },
    hovertemplate: `Mann–Whitney U (Low vs High)<br>n1 = ${mwLowHigh.na}, n2 = ${mwLowHigh.nb}<br>U = ${mwLowHigh.U.toFixed(1)}<br>z = ${mwLowHigh.z.toFixed(2)}<br>p = ${mwLowHigh.p.toExponential(2)}<extra></extra>`,
    showlegend: false,
  };

  const data: any = [refLine, control, lowDose, highDose, bracketLeft, bracketMid, bracketRight, bracketStars, starMarkers];

  const layout: any = {
    autosize: true,
    margin: { l: 56, r: 28, t: 36, b: 44 },
    hovermode: "closest",
    xaxis: {
      title: { text: "Condition", font: { size: 12 } },
      categoryorder: "array",
      categoryarray: groups as any,
      zeroline: false,
    },
    yaxis: {
      title: { text: "Effect (a.u.)", font: { size: 12 } },
      gridcolor: "rgba(0,0,0,0.1)",
      zeroline: false,
    },
    paper_bgcolor: "rgba(255,255,255,0)",
    plot_bgcolor: "rgba(255,255,255,0)",
    showlegend: false,
  };

  const config: any = { displayModeBar: false, responsive: true };

  return (
    <div className="group relative select-none overflow-hidden rounded-[10px] border border-black/10 bg-white/95 shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]">
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Scientific Box Plot (3 groups)
        </div>
        <div className="-mr-1 flex items-center">
          <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100" disabled>
            Open app
          </button>
        </div>
      </div>
      <div className="h-[420px] w-full p-2 text-neutral-800">
        <Plot style={{ width: "100%", height: "100%" }} data={data as any} layout={layout} config={mergeMapboxConfig(config)} />
      </div>
    </div>
  );
}

export function ScientificBoxPlotWindow() {
  function sample(n: number, mean: number, sd: number) {
    return Array.from({ length: n }, () => mean + sd * (Math.random() * 2 - 1));
  }

  const groups = ["Control", "Treatment"] as const;
  const yA = React.useMemo(() => sample(48, 0.05, 0.22), []);
  const yB = React.useMemo(() => sample(48, 0.32, 0.28), []);

  function mean(arr: number[]) { return arr.reduce((a, b) => a + b, 0) / arr.length; }
  function variance(arr: number[], m = mean(arr)) { return arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1); }
  function welchTTest(a: number[], b: number[]) {
    const ma = mean(a), mb = mean(b);
    const va = variance(a, ma), vb = variance(b, mb);
    const na = a.length, nb = b.length;
    const t = (ma - mb) / Math.sqrt(va / na + vb / nb);
    const df = ((va / na + vb / nb) ** 2) / ((va ** 2) / (na ** 2 * (na - 1)) + (vb ** 2) / (nb ** 2 * (nb - 1)));
    const z = Math.abs(t);
    const p = 2 * (1 - (function normalCdf(x: number) {
      const t = 1 / (1 + 0.2316419 * x);
      const d = 0.3989423 * Math.exp(-x * x / 2);
      let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
      return 1 - p;
    })(z));
    return { t, df, p };
  }
  function star(p: number) { return p < 1e-3 ? "***" : p < 1e-2 ? "**" : p < 5e-2 ? "*" : "ns"; }

  const { p, t } = welchTTest(yA, yB);
  const starLabel = star(p);

  const labelsA = yA.map((_, i) => `C-${String(i + 1).padStart(2, "0")}`);
  const labelsB = yB.map((_, i) => `T-${String(i + 1).padStart(2, "0")}`);

  const controlBox: any = {
    type: "box",
    name: groups[0],
    x: Array(yA.length).fill(groups[0]),
    y: yA,
    boxpoints: "outliers",
    marker: { color: "#6b7280", line: { color: "#374151", width: 1 } },
    line: { color: "#111827", width: 1 },
    fillcolor: "rgba(156,163,175,0.18)",
    hovertemplate: "<b>%{fullData.name}</b><extra></extra>",
    text: labelsA,
  };

  const treatmentBox: any = {
    type: "box",
    name: groups[1],
    x: Array(yB.length).fill(groups[1]),
    y: yB,
    boxpoints: "outliers",
    marker: { color: "#111827", line: { color: "#111827", width: 1 } },
    line: { color: "#111827", width: 1 },
    fillcolor: "rgba(255,213,0,0.25)",
    hovertemplate: "<b>%{fullData.name}</b><extra></extra>",
    text: labelsB,
  };

  controlBox.hovertemplate = "<b>%{text}</b><br>y = %{y:.3f}<extra>" + groups[0] + "</extra>";
  treatmentBox.hovertemplate = "<b>%{text}</b><br>y = %{y:.3f}<extra>" + groups[1] + "</extra>";

  const refLine: any = {
    type: "scatter",
    mode: "lines",
    name: "Reference",
    x: [groups[0], groups[1]],
    y: [0, 0],
    line: { width: 1.5, dash: "dot", color: "#9ca3af" },
    hovertemplate: "Reference line y=0<extra></extra>",
    showlegend: false,
  };

  const maxA = Math.max(...yA);
  const maxB = Math.max(...yB);
  const yTop = Math.max(maxA, maxB) + 0.15;
  const h = 0.06;
  const bracketLeft: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Significance ${starLabel} (p=${p.toExponential(2)})<extra></extra>`,
    x: [groups[0], groups[0]],
    y: [yTop - h, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };
  const bracketMid: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Significance ${starLabel} (p=${p.toExponential(2)})<extra></extra>`,
    x: [groups[0], groups[1]],
    y: [yTop, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };
  const bracketRight: any = {
    type: "scatter",
    mode: "lines",
    hovertemplate: `Significance ${starLabel} (p=${p.toExponential(2)})<extra></extra>`,
    x: [groups[1], groups[1]],
    y: [yTop - h, yTop],
    line: { color: "#ffd500", width: 2 },
    showlegend: false,
  };

  const data = [refLine, controlBox, treatmentBox, bracketLeft, bracketMid, bracketRight];

  const layout: any = {
    autosize: true,
    margin: { l: 56, r: 28, t: 32, b: 44 },
    hovermode: "closest",
    xaxis: {
      title: { text: "Condition", font: { size: 12 } },
      categoryorder: "array",
      categoryarray: groups as any,
      zeroline: false,
    },
    yaxis: {
      title: { text: "Effect (a.u.)", font: { size: 12 } },
      gridcolor: "rgba(0,0,0,0.1)",
      zeroline: false,
      // Ensure enough headroom for significance bracket
      range: [undefined, yTop + 0.2],
    },
    paper_bgcolor: "rgba(255,255,255,0)",
    plot_bgcolor: "rgba(255,255,255,0)",
    showlegend: false,
  };

  const config: any = { displayModeBar: false, responsive: true };

  return (
    <div className="group relative select-none overflow-hidden rounded-[10px] border border-black/10 bg-white/95 shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]">
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Scientific Box Plot
        </div>
        <div className="-mr-1 flex items-center">
          <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100" disabled>
            Open app
          </button>
        </div>
      </div>
      <div className="h-[420px] w-full p-2 text-neutral-800">
        <Plot style={{ width: "100%", height: "100%" }} data={data as any} layout={layout} config={mergeMapboxConfig(config)} />
      </div>
    </div>
  );
}

export function PlotTypeGalleryWindow() {
  const miniLayout = (): any => ({
    margin: { l: 8, r: 8, t: 6, b: 8 },
    height: 120,
    autosize: true,
    showlegend: false,
    xaxis: { showgrid: false, zeroline: false, showticklabels: false },
    yaxis: { showgrid: false, zeroline: false, showticklabels: false },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
  });

  const ink = "#6b7280";
  const gray = "#9ca3af";
  const gray2 = "#d1d5db";
  const fillSoft = "rgba(107,114,128,0.03)";
  const fillSofter = "rgba(107,114,128,0.015)";

  const xs = [1, 2, 3, 4, 5];
  const ys1 = [2, 3, 4, 3, 5];
  const ys2 = [1, 2, 2.5, 2, 3];
  const cats = ["A", "B", "C", "D"];
  const diverge = [0.45, -0.6, 0.25, -0.35];

  function correlatedPoints(n = 70, rho = 0.75) {
    const X: number[] = [], Y: number[] = [];
    for (let i = 0; i < n; i++) {
      const u1 = Math.random(), u2 = Math.random();
      const z1 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      const z2 = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
      const x = z1;
      const y = rho * z1 + Math.sqrt(1 - rho * rho) * z2;
      X.push(x); Y.push(y);
    }
    const scale = (a: number[]) => {
      const mn = Math.min(...a), mx = Math.max(...a);
      return a.map(v => 5 * (v - mn) / (mx - mn));
    };
    return { x: scale(X), y: scale(Y) };
  }
  const cloud = correlatedPoints();

  const rand = (n: number, m: number, s: number) =>
    Array.from({ length: n }, () => m + s * (Math.random() * 2 - 1));
  const boxGroups = [
    rand(40, 0.20, 0.22),
    rand(40, 0.45, 0.20),
    rand(40, 0.10, 0.18),
    rand(40, 0.35, 0.20),
  ];
  const violinGroups = [
    rand(80, 0.20, 0.20),
    rand(80, 0.45, 0.24),
    rand(80, 0.10, 0.18),
    rand(80, 0.35, 0.22),
  ];

  const heat = [
    [1, 3, 2, 0],
    [2, 1, 0, 1],
    [0, 1, 3, 2],
    [1, 2, 2, 1],
  ];

  const logitX = Array.from({ length: 60 }, (_, i) => i / 6);
  const k = 1.1, x0 = 4;
  const logitY = logitX.map(x => 1 / (1 + Math.exp(-k * (x - x0))));

  const fanX = Array.from({ length: 30 }, (_, i) => i);
  const base = fanX.map(t => 0.4 + 0.015 * t + 0.02 * Math.sin(t / 4));
  const sds  = fanX.map(t => 0.03 + 0.004 * t);
  const band = (kk: number) => ({ lo: base.map((m,i)=>m-kk*sds[i]), hi: base.map((m,i)=>m+kk*sds[i]) });
  const b95 = band(1.96), b80 = band(1.28), b50 = band(0.67);

  const sankey = {
    type: "sankey",
    arrangement: "snap",
    node: {
      label: ["","","","","",""],
      color: ["#e5e7eb", gray2, "#e5e7eb", "#e5e7eb", gray2, gray],
      pad: 8,
      line: { color: "rgba(0,0,0,0)", width: 0 },
      hoverinfo: "skip",
    },
    link: {
      source: [0,1,0,2,3],
      target: [2,2,3,4,5],
      value:  [3,2,2,2,3],
      color:  ["#f3f4f6","#e5e7eb","#e5e7eb","#d1d5db","#cbd5e1"],
      hoverinfo: "skip",
    },
    hoverinfo: "skip",
  } as any;

  // Map gallery card keys to shipped icon filenames in chart_icons
  const keyToIcon: Record<string, string> = {
    spine: 'spine.svg',
    scatterplot: 'scatterplot.svg',
    combo: 'line-column.svg',
    'xy-heatmap': 'XY-heatmap.svg',
    slope: 'slope-timeline.svg',
    boxplot: 'boxplot.svg',
    violin: 'violin.svg',
    logit: 'Logistic.svg',
    fan: 'ribbon-chart.svg',
    sankey: 'sankey.svg',
    paired: 'column-grouped.svg',
    donut: 'doughnut.svg',
    // additional icons to make a 5x5 grid
    area: 'area.svg',
    'bar-ordered': 'bar-ordered.svg',
    'bar-grouped': 'bar-grouped.svg',
    'bar-stacked-proportional': 'bar-stacked-proportional.svg',
    bubble: 'bubble.svg',
    bullet: 'bullet.svg',
    bump: 'bump.svg',
    candlestick: 'candlestick.svg',
    'circle-timeline': 'circle-timeline.svg',
    'column-ordered': 'column-ordered.svg',
    'column-stacked': 'column-stacked.svg',
    'dot-plot': 'dot-plot.svg',
    histogram: 'histogram.svg',
  };

  // 25 cards for a 5x5 grid (icons only)
  const iconCards: Array<{ key: keyof typeof keyToIcon | string; label: string }> = [
    { key: 'spine', label: 'Spine' },
    { key: 'scatterplot', label: 'Scatterplot' },
    { key: 'combo', label: 'Column + line' },
    { key: 'xy-heatmap', label: 'XY heatmap' },
    { key: 'slope', label: 'Slope' },
    { key: 'boxplot', label: 'Boxplot' },
    { key: 'violin', label: 'Violin plot' },
    { key: 'logit', label: 'Logit (sigmoid)' },
    { key: 'fan', label: 'Fan chart' },
    { key: 'sankey', label: 'Sankey' },
    { key: 'paired', label: 'Paired column' },
    { key: 'donut', label: 'Donut' },
    { key: 'area', label: 'Area' },
    { key: 'bar-ordered', label: 'Bar ordered' },
    { key: 'bar-grouped', label: 'Bar grouped' },
    { key: 'bar-stacked-proportional', label: 'Bar 100%' },
    { key: 'bubble', label: 'Bubble' },
    { key: 'bullet', label: 'Bullet' },
    { key: 'bump', label: 'Bump' },
    { key: 'candlestick', label: 'Candlestick' },
    { key: 'circle-timeline', label: 'Circle timeline' },
    { key: 'column-ordered', label: 'Column ordered' },
    { key: 'column-stacked', label: 'Column stacked' },
    { key: 'dot-plot', label: 'Dot plot' },
    { key: 'histogram', label: 'Histogram' },
  ];

  const cards = [
    { key: "spine", label: "Spine", data: [ { type: "bar", orientation: "h", x: diverge, y: cats, marker: { color: gray }, hovertemplate: "<b>Spine</b><extra></extra>" } ], layout: { ...miniLayout(), xaxis: { ...miniLayout().xaxis, range: [-1, 1] }, yaxis: { ...miniLayout().yaxis, type: "category" }, shapes: [{ type: "line", x0: 0, x1: 0, y0: 0, y1: 1, xref: "x", yref: "paper", line: { color: ink, width: 1 } }] }, },
    { key: "scatterplot", label: "Scatterplot", data: [ { type: "scatter", mode: "markers", x: cloud.x, y: cloud.y, marker: { size: 6, color: gray }, hovertemplate: "<b>Scatterplot</b><extra></extra>" } ], layout: miniLayout(), },
    { key: "combo", label: "Column + line", data: [ { type: "bar", x: xs, y: ys1, marker: { color: gray2 }, hovertemplate: "<b>Column + line</b><extra></extra>" }, { type: "scatter", mode: "lines", x: xs, y: ys2, line: { color: ink, width: 1 }, hovertemplate: "<b>Column + line</b><extra></extra>" } ], layout: miniLayout(), },
    { key: "xy-heatmap", label: "XY heatmap", data: [ { type: "heatmap", z: heat, colorscale: [[0,"#f9fafb"],[1,"#d1d5db"]], hovertemplate: "<b>XY heatmap</b><extra></extra>" } ], layout: miniLayout(), },
    { key: "slope", label: "Slope", data: cats.map((_, i) => ({ type: "scatter", mode: "lines+markers", x: [0, 1], y: [ys1[i % ys1.length] / 5 + 0.2, ys2[i % ys2.length] / 5 + 0.6], line: { color: ink, width: 1 }, marker: { size: 6, color: i % 2 ? gray2 : gray }, hovertemplate: "<b>Slope</b><extra></extra>" })), layout: { ...miniLayout(), xaxis: { ...miniLayout().xaxis, range: [-0.2, 1.2] } }, },
    { key: "boxplot", label: "Boxplot", data: boxGroups.map((g) => ({ type: "box", y: g, boxpoints: false, line: { color: ink, width: 0.5 }, fillcolor: fillSofter, hoverinfo: "skip", showlegend: false })), layout: miniLayout(), },
    { key: "violin", label: "Violin plot", data: violinGroups.map((g) => ({ type: "violin", y: g, points: false, line: { color: ink, width: 0.5 }, fillcolor: fillSofter, hoverinfo: "skip", showlegend: false })), layout: miniLayout(), },
    { key: "logit", label: "Logit (sigmoid)", data: [ { type: "scatter", mode: "lines", x: logitX, y: logitY, line: { color: ink, width: 1 }, hovertemplate: "<b>Logit (sigmoid)</b><extra></extra>" }, { type: "scatter", mode: "lines", x: logitX, y: logitX.map(() => 0.98), line: { color: gray2, width: 0.5, dash: "dash" }, hoverinfo: "skip" } ], layout: miniLayout(), },
    { key: "fan", label: "Fan chart (projection)", data: [ { type: "scatter", mode: "lines", x: fanX, y: b95.lo, line: { width: 0 }, hoverinfo: "skip" }, { type: "scatter", mode: "lines", x: fanX, y: b95.hi, fill: "tonexty", fillcolor: fillSofter, line: { width: 0 }, hoverinfo: "skip" }, { type: "scatter", mode: "lines", x: fanX, y: b80.hi, fill: "tonexty", fillcolor: fillSoft,   line: { width: 0 }, hoverinfo: "skip" }, { type: "scatter", mode: "lines", x: fanX, y: b50.hi, fill: "tonexty", fillcolor: "rgba(107,114,128,0.05)", line: { width: 0 }, hoverinfo: "skip" }, { type: "scatter", mode: "lines", x: fanX, y: base,   line: { color: ink, width: 1 }, hoverinfo: "skip" } ], layout: miniLayout(), },
    { key: "sankey", label: "Sankey", data: [sankey], layout: miniLayout(), },
    { key: "paired", label: "Paired column", data: [ { type: "bar", x: cats, y: [3, 2, 4, 3], marker: { color: gray },  hovertemplate: "<b>Paired column</b><extra></extra>" }, { type: "bar", x: cats, y: [2, 3, 3, 2], marker: { color: gray2 }, hovertemplate: "<b>Paired column</b><extra></extra>" } ], layout: { ...miniLayout(), barmode: "group" }, },
    { key: "donut", label: "Donut", data: [{ type: "pie", values: [30, 20, 15, 35], labels: ["A","B","C","D"], hole: 0.55, textinfo: "none", marker: { colors: ["#d1d5db", "#e5e7eb", "#f3f4f6", gray] }, hovertemplate: "<b>Donut</b><extra></extra>" }], layout: { ...miniLayout(), showlegend: false }, },
  ];

  return (
    <div className="group relative select-none overflow-hidden rounded-[10px] border border-black/10 bg-white/95 shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]">
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Plot type gallery
        </div>
        <div className="-mr-1 flex items-center">
          <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100" disabled>
            Open app
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 p-2">
        {iconCards.map((c) => {
          const iconFile = keyToIcon[c.key] || 'line.svg';
          const src = resolveIconSrc(iconFile);
          return (
            <div key={c.key} className="group/card relative rounded-md border border-black/10 bg-white p-2 shadow-sm h-[100px] overflow-hidden flex items-center justify-center">
              <div className="absolute left-2 top-2 z-10 rounded bg-white/90 px-1.5 py-0.5 text-[11px] text-neutral-700 opacity-0 group-hover/card:opacity-100 pointer-events-none shadow-sm">
                {c.label}
              </div>
              <img
                src={src}
                alt={c.label}
                className="h-[64px] w-auto object-contain mx-auto grayscale contrast-75 transition-[filter] duration-200 group-hover/card:grayscale-0 group-hover/card:contrast-100"
                loading="lazy"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LumiChatWindow({
  width = 520,
  height = 560,
  centered = true,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
}) {
  // Original dimensions from lumichat.tsx
  const originalWidth = 520;
  const originalHeight = 560;
  
  let finalWidth = width;
  let finalHeight = height;
  let fitScale = 1;
  
  if (centered) {
    // Center within parent container
    // Layout: max-w-5xl (1024px), grid 12 cols, right panel is col-span-8 (66.67%)
    // Right panel padding: p-12 = 48px each side on large screens
    // Container width: 1024px
    // Right panel width: (1024px * 8/12) = 682.67px
    // Available width (minus padding): 682.67px - 96px = 586.67px
    // Available height: min-h-[680px] - 96px (p-12 top+bottom) = 584px (with buffer)
    const containerMaxWidth = 1024; // max-w-5xl
    const rightPanelRatio = 8 / 12; // col-span-8
    const padding = 48; // p-12 on large screens
    const sectionHeight = 680; // min-h-[680px] - increased to fit window with buffer
    
    const rightPanelWidth = containerMaxWidth * rightPanelRatio;
    const availableWidth = rightPanelWidth - (padding * 2);
    const availableHeight = sectionHeight - (padding * 2);
    
    // Scale window to fit within available space (maintain aspect ratio)
    const widthScale = availableWidth / originalWidth;
    const heightScale = availableHeight / originalHeight;
    fitScale = Math.min(widthScale, heightScale, 1); // Don't scale up, only down
    
    // Calculate scaled dimensions
    finalWidth = originalWidth * fitScale;
    finalHeight = originalHeight * fitScale;
  }
  
  const wrapperStyle: React.CSSProperties = {
    width: finalWidth,
    height: finalHeight,
    transformOrigin: centered ? 'center center' : 'top left',
    overflow: 'visible',
  };

  if (centered) {
    const containerMaxWidth = 1024;
    const rightPanelRatio = 8 / 12;
    const padding = 48;
    const sectionHeight = 680;
    
    const rightPanelWidth = containerMaxWidth * rightPanelRatio;
    const availableWidth = rightPanelWidth - (padding * 2);
    const availableHeight = sectionHeight - (padding * 2);
    
    const centerOffsetX = (availableWidth - finalWidth) / 2;
    const centerOffsetY = (availableHeight - finalHeight) / 2;
    
    wrapperStyle.position = 'absolute';
    wrapperStyle.left = `${centerOffsetX + padding}px`;
    wrapperStyle.top = `${centerOffsetY + padding}px`;
  }

  return (
    <div style={wrapperStyle}>
      <LandingChatDemo 
        width={finalWidth} 
        height={finalHeight} 
        centered={false}
        autoCursor={true}
      />
    </div>
  );
}

export function PlotEditorWindow({
  width = 700,
  height = 457,
  centered = false,
  style,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
  style?: React.CSSProperties;
}) {
  // --- UI state (plot typography) ---
  const [fontSize, setFontSize] = React.useState(12);
  const [fontColor, setFontColor] = React.useState("#000000");
  const [fontWeight, setFontWeight] = React.useState<400 | 500 | 600 | 700>(400);
  const [fontFamily, setFontFamily] = React.useState(
    "Inter, system-ui, sans-serif"
  );

  // --- Chart toggles ---
  const [paletteOpacity, setPaletteOpacity] = React.useState(80);
  const [showGrid, setShowGrid] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<
    "style" | "axes" | "series" | "points" | "annotations"
  >("style");

  // Visual constants (no user controls for these, by request)
  const PAPER = "#ffffff";
  const PLOT = "#ffffff";
  
  // --- Palettes ---
  const themePresets = React.useMemo(
    () => [
      // Extracted from attached SVGs (unordered swatches)
      { name: "SVG–05", swatches: ["#820724", "#92C5DE", "#0B3C72"] },
      { name: "SVG–06 (blue)", swatches: ["#DDEAF5", "#5CA3D0", "#14549F"] },
      { name: "SVG–07", swatches: ["#BF812D", "#C7EAE5", "#01665E"] },
      // Additional user-provided selectable palettes
      { name: "CB Classic", swatches: ["#0072B2", "#E69F00", "#009E73"] },
      { name: "Viridis Trio", swatches: ["#440154", "#2A788E", "#7AD151"] },
      { name: "Navy-Gray-Yellow", swatches: ["#00204C", "#6E7E8E", "#FDE725"] },
    ],
    []
  );

  // --- Demo palette loop (default start on BLUE palette index = 1) ---
  const [currentThemeIndex, setCurrentThemeIndex] = React.useState(1);
  const themeIndexRef = React.useRef(currentThemeIndex);
  React.useEffect(() => {
    themeIndexRef.current = currentThemeIndex;
  }, [currentThemeIndex]);

  const [hoveredThemeIndex, setHoveredThemeIndex] = React.useState<number | null>(null);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showCursor, setShowCursor] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false); // Start as false, will be triggered by scroll
  const [isChangingColor, setIsChangingColor] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const paletteBlockRef = React.useRef<HTMLDivElement>(null);
  const paletteButtonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownItemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const cursorControls = useAnimation();

  // blue → svg05 → svg07 then stop
  const demoIndices = React.useMemo(() => [1, 0, 2], []);

  // --- Color utilities to normalize roles by lightness
  // Rule (default): Industry = lightest, Power = middle, Transport = darkest
  const hexToRgb = (hex: string) => {
    const v = hex.replace("#", "");
    const r = parseInt(v.slice(0, 2), 16) / 255;
    const g = parseInt(v.slice(2, 4), 16) / 255;
    const b = parseInt(v.slice(4, 6), 16) / 255;
    return { r, g, b };
  };
  const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    const R = srgbToLinear(r);
    const G = srgbToLinear(g);
    const B = srgbToLinear(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };

  const pickThemeFromPreset = React.useCallback(
    (presetIndex: number) => {
      const p = themePresets[presetIndex];
      const sorted = p.swatches.slice().sort((a, b) => luminance(a) - luminance(b)); // darkest..lightest
      // Default mapping
      let mapping = { transport: sorted[0], power: sorted[1], industry: sorted[2] } as const;
      // Exception (per user): first palette swaps mid/dark roles between power and transport
      if (presetIndex === 0) {
        mapping = { power: sorted[0], transport: sorted[1], industry: sorted[2] } as const;
      }
      return mapping;
    },
    [themePresets]
  );

  const currentPresetIndex = hoveredThemeIndex !== null ? hoveredThemeIndex : currentThemeIndex;
  const theme = React.useMemo(() => pickThemeFromPreset(currentPresetIndex), [currentPresetIndex, pickThemeFromPreset]);

  // --- Data ---
  const countries = ["Demeria", "Borealis Union", "Elysia", "Auroria", "Celestria"];
  const power = [660.0, 990.0, 1650.0, 2420.0, 4400.0];
  const transport = [495.0, 715.0, 660.0, 1870.0, 1650.0];
  const industry = [418.0, 605.0, 990.0, 1320.0, 2420.0];

  const rows = countries.map((c, i) => {
    const total = power[i] + transport[i] + industry[i];
    const perr = Math.max(10, Math.round(power[i] * 0.07));
    const terr = Math.max(10, Math.round(transport[i] * 0.07));
    const ierr = Math.max(10, Math.round(industry[i] * 0.07));
    return {
      country: c,
      power: power[i],
      powerErr: perr,
      transport: transport[i],
      transportErr: terr,
      industry: industry[i],
      industryErr: ierr,
      total,
      shares: {
        power: (power[i] / total) * 100,
        transport: (transport[i] / total) * 100,
        industry: (industry[i] / total) * 100,
      },
    } as const;
  });

  const opacity = paletteOpacity / 100;
  const yMax = 5000;

  // --- Scroll detection: Start animation 1.2s after section is visible ---
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating) {
            // Section is visible, start animation after 1.2s delay
            setTimeout(() => {
              setIsAnimating(true);
            }, 1200);
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the component is visible
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isAnimating]);

  // --- Demo cursor animation ---
  React.useEffect(() => {
    if (!isAnimating) return;
    let cancelled = false;
    let runs = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

    const run = async () => {
      const curr = themeIndexRef.current;
      const posInSeq = demoIndices.indexOf(curr);
      const nextIndex = demoIndices[(posInSeq === -1 ? 0 : (posInSeq + 1) % demoIndices.length)];

      const container = paletteBlockRef.current;
      const buttonEl = paletteButtonRef.current;
      if (!container || !buttonEl) return;

      // Calculate positions relative to the palette container
      // Since we're using CSS transform scale, getBoundingClientRect() returns scaled coordinates
      // But the cursor is positioned in the scaled coordinate system, so we need to account for scale
      const scaleFactor = width / 980;
      const crect = container.getBoundingClientRect();
      const brect = buttonEl.getBoundingClientRect();
      
      // Convert viewport coordinates to coordinates relative to container, then scale for the inner div
      // Account for cursor tip offset: TIP_X = 18, TIP_Y = 13
      const toButton = {
        left: (brect.right - crect.left - 18) / scaleFactor,
        top: (brect.top - crect.top + brect.height / 2 - 13) / scaleFactor,
        opacity: 1,
        scale: 1,
      } as any;

      // Set cursor visible and position it immediately at the button before animating
      setShowCursor(true);
      // Set initial position immediately so cursor is visible from the start
      cursorControls.set({
        ...toButton,
        opacity: 1,
      });
      
      // Small delay to ensure DOM has updated
      await sleep(50);

      await cursorControls.start({
        ...toButton,
        transition: { type: "tween", ease: "easeInOut", duration: 0.8 },
      });
      if (cancelled) return;

      // tap to open
      await cursorControls.start({ scale: 0.92, transition: { duration: 0.12 } });
      await cursorControls.start({ scale: 1, transition: { duration: 0.12 } });

      setShowDropdown(true);
      await sleep(220);

      const itemEl = dropdownItemRefs.current[nextIndex];
      if (itemEl) {
        const irect = itemEl.getBoundingClientRect();
        // Account for cursor tip offset: TIP_X = 18, TIP_Y = 13
        const toItem = {
          left: (irect.right - crect.left - 18) / scaleFactor,
          top: (irect.top - crect.top + irect.height / 2 - 13) / scaleFactor,
        } as any;
        await cursorControls.start({
          ...toItem,
          transition: { type: "tween", ease: "easeInOut", duration: 0.8 },
        });
        if (cancelled) return;
      }

      setHoveredThemeIndex(nextIndex);
      await cursorControls.start({ scale: 0.92, transition: { duration: 0.1 } });
      await cursorControls.start({ scale: 1, transition: { duration: 0.1 } });

      setIsChangingColor(true);
      setCurrentThemeIndex(nextIndex);
      await sleep(250);
      if (cancelled) return;

      setShowDropdown(false);
      setIsChangingColor(false);
      setHoveredThemeIndex(null);
      runs += 1;

      if (runs >= 3) {
        await cursorControls.start({ opacity: 0, transition: { duration: 0.4 } });
        setShowCursor(false);
        setIsAnimating(false);
        return;
      }
    };

    // Start immediately when isAnimating becomes true (already delayed by scroll observer)
    const kickoffId = setTimeout(() => {
      if (cancelled) return;
      run();
      intervalId = setInterval(() => {
        if (cancelled || !isAnimating) {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        run();
      }, 6000);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(kickoffId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAnimating, cursorControls, demoIndices, width]);

  // --- Tooltip ---
  const TooltipBox: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload; // hovered country row
    const items = [
      { key: "power", label: "Power", color: theme.power, share: row.shares.power, val: row.power },
      { key: "transport", label: "Transport", color: theme.transport, share: row.shares.transport, val: row.transport },
      { key: "industry", label: "Industry", color: theme.industry, share: row.shares.industry, val: row.industry },
    ];
    const headerVal = payload[0]?.value ?? 0;
    const headerName = payload[0]?.name ?? "Value";
    return (
      <div
        style={{
          background: "#fff",
          border: `1px solid ${fontColor}`,
          padding: 10,
          maxWidth: 300,
          color: fontColor,
          fontFamily,
        }}
      >
        <div style={{ fontFamily, fontWeight, marginBottom: 6 }}>
          {label}: {headerName} = {Number(headerVal).toLocaleString()}
        </div>
        {items.map((it) => (
          <div key={it.key} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: fontSize, marginBottom: 2 }}>Country: {row.country}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: fontSize }}>
              <span style={{ width: 8, height: 8, background: it.color, display: "inline-block" }} />
              <span>Sector: {it.label}</span>
            </div>
            <div style={{ fontSize: fontSize }}>Emissions: {Math.round(it.val).toLocaleString()} Mt CO2</div>
            <div style={{ fontSize: fontSize }}>Sector share: {it.share.toFixed(1)}%</div>
            <div style={{ fontSize: fontSize }}>Total: {Math.round(row.total).toLocaleString()} Mt CO2</div>
          </div>
        ))}
      </div>
    );
  };

  // Calculate scale factor: 700 / 980 = 0.7143
  const scaleFactor = width / 980;
  const originalWidth = 980;
  const originalHeight = 640;

  const wrapperStyle: React.CSSProperties = {
    width: width, // Use scaled width in layout
    height: height, // Use scaled height in layout
    transformOrigin: centered ? 'center center' : 'top left',
    overflow: 'hidden', // Hide overflow from scaled content
    ...style, // Merge any passed-in styles
  };

  const innerStyle: React.CSSProperties = {
    width: originalWidth,
    height: originalHeight,
    transform: `scale(${scaleFactor})`,
    transformOrigin: 'top left',
  };

  if (centered) {
    wrapperStyle.position = 'absolute';
    wrapperStyle.left = '50%';
    wrapperStyle.top = '50%';
    wrapperStyle.transform = 'translate(-50%, -50%)';
    innerStyle.transformOrigin = 'center center';
    innerStyle.transform = `translate(${(originalWidth - width) / 2}px, ${(originalHeight - height) / 2}px) scale(${scaleFactor})`;
  }

  return (
    <div ref={containerRef} style={wrapperStyle}>
      <div style={innerStyle}>
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={[
        "group relative select-none overflow-hidden flex flex-col rounded-[10px]",
        "shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]",
        "border border-black/10",
        centered ? "" : "",
      ].join(" ")}
      style={{ width: originalWidth, height: originalHeight, backgroundColor: PAPER }}
    >
      {/* Window bar */}
      <div
        className="relative flex h-7 items-center justify-between border-b border-black/10 px-2"
        style={{ background: PAPER }}
      >
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs"
          style={{ color: fontColor }}
        >
          Plot Editor
        </div>
        <div className="-mr-1 flex items-center">
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs hover:bg-neutral-100"
            style={{ color: fontColor }}
            disabled
          >
            Open Editor
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100%-28px)] w-full overflow-hidden">
        {/* Plot area */}
        <div className="flex-1 flex flex-col border-r border-black/10" style={{ flexBasis: "60%", flexGrow: 0 }}>
          <div className="flex-1 p-4 overflow-hidden" style={{ background: PAPER }}>
            <div className="h-full w-full relative">
              {/* Title + subtitle */}
              <div className="absolute left-4 top-2 z-10" style={{ color: fontColor }}>
                <div style={{ fontFamily, fontSize: fontSize + 6, fontWeight, marginBottom: 8 }}>CO2 Emissions by Sector and Country</div>
                <div style={{ fontFamily, fontSize: fontSize, fontWeight, marginBottom: 16 }}>ANOVA F=1.282, p = 0.313</div>
            </div>

              {/* Chart canvas with background (plot area) */}
              <div className="absolute inset-0 top-10" style={{ background: PLOT, borderRadius: 6 }} />

              <ResponsiveContainer width="100%" height="100%">
                {/* Grouped bars with error bars */}
                <BarChart data={rows} margin={{ left: 56, right: 16, top: 84, bottom: 40 }} barCategoryGap="28%" barGap={6}>
                  <CartesianGrid vertical={false} horizontal={showGrid} stroke="rgba(0,0,0,0.08)" />
                  <XAxis
                    type="category"
                    dataKey="country"
                    tick={{ fontSize: fontSize, fill: fontColor, fontFamily }}
                    interval={0}
                    height={44}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="number"
                    tickFormatter={(v) => Number(v).toLocaleString()}
                    tick={{ fontSize: fontSize, fill: fontColor, fontFamily }}
                    axisLine={false}
                    tickLine={false}
                    tickCount={6}
                    domain={[0, yMax]}
                    width={44}
                  >
                    <Label
                      value="CO2 Emissions (Mt CO2)"
                      position="left"
                      angle={-90}
                      offset={12}
                      style={{
                        textAnchor: "middle",
                        fontFamily,
                        fontSize: fontSize,
                        color: fontColor,
                        fontWeight,
                      }}
                    />
                  </YAxis>
                  <Tooltip cursor={{ stroke: fontColor, strokeDasharray: "3 3" }} content={<TooltipBox />} wrapperStyle={{ zIndex: 20 }} />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="square"
                    iconSize={10}
                    wrapperStyle={{
                      top: fontSize + 20 + 8, // Align with stats line: fontSize + 20 (original) + 8px margin between title and stats
                      right: 16,
                      fontFamily,
                      fontSize: fontSize,
                      color: fontColor,
                      fontWeight,
                    }}
                  />

                  <Bar dataKey="industry" name="Industry" fill={theme.industry} fillOpacity={opacity} isAnimationActive={false}>
                    <ErrorBar dataKey="industryErr" width={4} stroke={theme.industry} strokeWidth={1.25} strokeOpacity={1} direction="y" />
                  </Bar>
                  <Bar dataKey="power" name="Power" fill={theme.power} fillOpacity={opacity} isAnimationActive={false}>
                    <ErrorBar dataKey="powerErr" width={4} stroke={theme.power} strokeWidth={1.25} strokeOpacity={1} direction="y" />
                  </Bar>
                  <Bar dataKey="transport" name="Transport" fill={theme.transport} fillOpacity={opacity} isAnimationActive={false}>
                    <ErrorBar dataKey="transportErr" width={4} stroke={theme.transport} strokeWidth={1.25} strokeOpacity={1} direction="y" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
              </div>
            </div>

        {/* Controls */}
        <div
          className="border-l border-black/10"
          style={{ width: 340, flexShrink: 0, minWidth: 340, display: "flex", flexDirection: "column", background: PAPER }}
        >
          {/* Header */}
          <div className="h-12 px-4 border-b border-black/10 flex items-center" style={{ background: PAPER }}>
            <div className="flex items-center justify-between w-full">
              <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: fontColor }}>Plot Editor</h3>
              <div className="flex items-center gap-1">
                {/* disabled per request */}
                <button className="h-8 w-8 p-0 flex items-center justify-center rounded opacity-40 cursor-not-allowed pointer-events-none" style={{ color: fontColor }} disabled aria-disabled="true" title="Undo">
                  <Undo className="h-4 w-4" />
                </button>
                <button className="h-8 w-8 p-0 flex items-center justify-center rounded opacity-40 cursor-not-allowed pointer-events-none" style={{ color: fontColor }} disabled aria-disabled="true" title="Redo">
                  <Redo className="h-4 w-4" />
                </button>
                    </div>
                  </div>
                  </div>
          
          {/* Tabs */}
          <div className="mx-4 mt-2 grid grid-cols-5 w-auto border-b border-black/10" role="tablist">
            {(
              [
                ["style", <Palette key="p" className="h-3 w-3" />, "Style"],
                ["axes", <Settings2 key="a" className="h-3 w-3" />, "Axes"],
                ["series", <TrendingUp key="s" className="h-3 w-3" />, "Series"],
                ["points", <CircleDot key="c" className="h-3 w-3" />, "Points"],
                ["annotations", <Type key="t" className="h-3 w-3" />, "Annotations"],
              ] as const
            ).map(([key, icon, label]) => {
              const disabled = key !== "style";
              return (
                <button
                  key={key}
                  onClick={disabled ? undefined : () => setActiveTab(key as any)}
                  disabled={disabled}
                  aria-disabled={disabled}
                  tabIndex={disabled ? -1 : 0}
                  className={`flex h-9 items-center justify-center gap-1 px-2 border-b-2 transition-colors ${
                    activeTab === key
                      ? "border-[#ffd500] text-neutral-900 font-medium"
                      : disabled
                        ? "border-transparent text-neutral-400 cursor-not-allowed opacity-50"
                        : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                  style={{ color: disabled ? undefined : fontColor }}
                >
                  {icon}
                  <span className="text-xs">{label}</span>
                  </button>
              );
            })}
                </div>

          {/* Panel content */}
          <div 
            className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-6" 
            style={{ 
              background: PAPER,
              overscrollBehaviorY: 'contain',
              overscrollBehaviorX: 'contain',
              scrollbarGutter: 'stable both-edges'
            }}
          >
            {activeTab === "style" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-neutral-500" />
                  <h4 className="text-sm font-medium" style={{ color: fontColor }}>Design & output</h4>
                  </div>

                {/* Palette */}
                <div className="relative" ref={paletteBlockRef}>
                  <label className="text-xs text-neutral-600 mb-2 block">Categorical palette</label>
                  <button
                    ref={paletteButtonRef}
                    className="w-full h-6 rounded border border-neutral-300 bg-white flex items-center px-2 cursor-pointer hover:border-neutral-400 transition-all"
                    onClick={() => {
                      setIsAnimating(false);
                      setShowDropdown((v) => !v);
                      setShowCursor(false);
                    }}
                    style={{
                      borderColor: showDropdown ? "#ffd500" : undefined,
                      boxShadow: showDropdown ? "0 0 0 2px rgba(255,213,0,0.2)" : undefined,
                    }}
                  >
                    <div
                      className="flex-1 h-3 rounded transition-all duration-500"
                      style={{
                        background: `linear-gradient(to right, ${theme.power}, ${theme.transport}, ${theme.industry})`,
                        opacity: isChangingColor ? 0.7 : 1,
                      }}
                    />
                    <svg
                      className="w-3 h-3 ml-2 text-neutral-400 transition-transform"
                      style={{ transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)" }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showDropdown && (
                    <div 
                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-300 rounded shadow-lg z-50 p-1 max-h-64 overflow-y-auto overscroll-contain"
                      style={{ overscrollBehaviorY: 'contain', overscrollBehaviorX: 'contain' }}
                    >
                      {themePresets.map((_, idx) => {
                        const isHovered = hoveredThemeIndex === idx;
                        const isSelected = currentThemeIndex === idx;
                        const mapped = pickThemeFromPreset(idx);
                        return (
                          <button
                            key={idx}
                            ref={(el) => (dropdownItemRefs.current[idx] = el)}
                            className={`w-full p-2 rounded transition-all ${
                              isSelected ? "bg-[#ffd500]/10 border border-[#ffd500]" : "border border-transparent hover:bg-neutral-50"
                            } ${isHovered ? "bg-neutral-100" : ""}`}
                            onMouseEnter={() => {
                              setIsAnimating(false);
                              setHoveredThemeIndex(idx);
                            }}
                            onMouseLeave={() => {
                              setHoveredThemeIndex(null);
                            }}
                            onClick={() => {
                              setIsAnimating(false);
                              setCurrentThemeIndex(idx);
                              setShowDropdown(false);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="h-3 flex-1 rounded"
                                style={{ background: `linear-gradient(to right, ${mapped.power}, ${mapped.transport}, ${mapped.industry})` }}
                              />
                  </div>
                  </button>
                        );
                      })}
                </div>
                  )}

                  {showCursor && (
                    <motion.div
                      className="absolute pointer-events-none z-50"
                      initial={false}
                      animate={cursorControls}
                      style={{ transformOrigin: "18px 13px" }}
                    >
                      <svg width="22" height="26" viewBox="0 0 24 28" fill="none" className="drop-shadow-md" aria-hidden="true">
                        {/* classic arrow pointer with white outline */}
                        <path
                          d="M3 2 L3 22 L9 16 L12.5 24 L14.7 23 L11.3 15 L19.5 15 Z"
                          fill="#111111"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  )}
                  </div>

                {/* Palette opacity */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-neutral-700">Palette opacity</label>
                    <span className="text-xs text-neutral-500 font-mono">{paletteOpacity}%</span>
                  </div>
                  <input
                    type="range"
                    min={20}
                    max={100}
                    value={paletteOpacity}
                    onChange={(e) => setPaletteOpacity(Number(e.target.value))}
                    className="w-full h-1.5 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-[#ffd500]"
                  />
                </div>

                {/* Typography */}
                <div className="space-y-0">
                  <label className="text-xs text-neutral-600 mb-2 block">Typography</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-neutral-600">Font family</label>
                      <select
                        className="mt-1 w-full h-8 text-xs border border-neutral-300 rounded px-2 bg-white"
                        value={fontFamily}
                        onChange={(e) => setFontFamily(e.target.value)}
                      >
                        <option value={"Inter, system-ui, sans-serif"}>Inter</option>
                        <option value={'"JetBrains Mono", ui-monospace, Menlo, Consolas, "Liberation Mono", monospace'}>
                          JetBrains Mono
                        </option>
                        <option value={'Georgia, "Times New Roman", serif'}>Georgia</option>
                        <option value={'Arial, Helvetica, sans-serif'}>Arial</option>
                      </select>
              </div>
                    <div>
                      <label className="text-xs text-neutral-600">Font size</label>
                      <input
                        type="number"
                        value={fontSize}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                        className="mt-1 w-full h-8 text-xs border border-neutral-300 rounded px-2"
                        min={8}
                        max={24}
                      />
            </div>
                    <div>
                      <label className="text-xs text-neutral-600">Font color</label>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="color"
                          value={fontColor}
                          onChange={(e) => setFontColor(e.target.value)}
                          className="h-8 w-12 rounded border border-neutral-300"
                        />
                        <span className="text-[10px] font-mono text-neutral-700">{fontColor}</span>
          </div>
        </div>
                    <div>
                      <label className="text-xs text-neutral-600">Font weight</label>
                      <select
                        className="mt-1 w-full h-8 text-xs border border-neutral-300 rounded px-2 bg-white"
                        value={fontWeight}
                        onChange={(e) => setFontWeight(Number(e.target.value) as any)}
                      >
                        <option value={400}>Regular</option>
                        <option value={500}>Medium</option>
                        <option value={600}>Semibold</option>
                        <option value={700}>Bold</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "axes" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-neutral-500" />
                  <h4 className="text-sm font-medium">Axes & Scales</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-neutral-700">Grid lines</label>
                <button 
                      onClick={() => setShowGrid((v) => !v)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        showGrid ? "bg-[#ffd500]" : "bg-neutral-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          showGrid ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                </button>
              </div>
            </div>
          </div>
            )}

            {activeTab === "series" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-neutral-500" />
                  <h4 className="text-sm font-medium">Series & Marks</h4>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Power", c: theme.power },
                    { label: "Transport", c: theme.transport },
                    { label: "Industry", c: theme.industry },
                  ].map(({ label, c }) => (
                    <div key={label}>
                      <label className="text-xs text-neutral-700 mb-1.5 block">{label}</label>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-16 rounded border-2 border-neutral-300" style={{ backgroundColor: c }} />
                        <span className="text-xs font-mono text-neutral-500">{c}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "points" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CircleDot className="h-4 w-4 text-neutral-500" />
                  <h4 className="text-sm font-medium">Individual Points</h4>
                </div>
                <div className="text-xs text-neutral-600">Select bars to customize individual markers.</div>
              </div>
            )}

            {activeTab === "annotations" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-neutral-500" />
                  <h4 className="text-sm font-medium">Annotations</h4>
                </div>
                <div className="text-xs text-neutral-600">Add text annotations and shapes to highlight key insights.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
      </div>
    </div>
  );
}


